import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ManagerDashboard from './ManagerDashboard';
import {
  Bus, MapPin, Clock, Star, ArrowRight, Phone, Mail,
  Calendar, Users, Shield, Award, Search, Loader2, ChevronDown, X, AlertTriangle
} from 'lucide-react';
import Swal from 'sweetalert2';
import { routeApi, cityApi, scheduleApi, routeCityApi } from '../services/api';
import { getSriLankaDateString, getMinSelectableDate, getMaxSelectableDate } from '../utils/dateUtils';
import { COMPANY_CONFIG } from '../config/config';

const Dashboard = () => {
  const { user, isRole, ROLES } = useAuth();
  const [fromCityId, setFromCityId] = useState('');
  const [toCityId, setToCityId] = useState('');
  const [travelDate, setTravelDate] = useState(getSriLankaDateString());
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false); // Track if search has been performed
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  
  // Timetable modal states
  const [isTimetableModalOpen, setIsTimetableModalOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [timetableData, setTimetableData] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  
  // Searchable dropdown states
  const [fromSearchTerm, setFromSearchTerm] = useState('');
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [toDropdownOpen, setToDropdownOpen] = useState(false);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  const resultsSectionRef = useRef(null);

  // Format date for display in DD/MM/YYYY format
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target)) {
        setFromDropdownOpen(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target)) {
        setToDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // SearchableDropdown Component
  const SearchableDropdown = ({ 
    label, 
    icon, 
    placeholder, 
    value, 
    searchTerm, 
    setSearchTerm, 
    dropdownOpen, 
    setDropdownOpen, 
    dropdownRef, 
    onChange, 
    options, 
    loading, 
    disabled 
  }) => {
    const filteredOptions = options
      .filter(option => 
        option.cityName.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.cityName.localeCompare(b.cityName));

    const selectedCity = options.find(city => city.cityID.toString() === value);

    const handleSelect = (city) => {
      onChange(city.cityID.toString());
      setSearchTerm('');
      setDropdownOpen(false);
    };

    const handleClear = () => {
      onChange('');
      setSearchTerm('');
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2 lg:mb-3">
          {icon}
          {label}
        </label>
        <div className="relative">
          <input
            type="text"
            value={selectedCity ? selectedCity.cityName : ''}
            readOnly
            onClick={() => setDropdownOpen(!dropdownOpen)}
            placeholder={loading ? 'Loading cities...' : placeholder}
            className="w-full px-4 py-3 lg:py-4 border border-blue-400 rounded-xl lg:rounded-2xl focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-blue-50 text-blue-900 text-base lg:text-lg transition-all duration-200 pr-10 cursor-pointer"
            disabled={loading || disabled}
            required
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {value && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="text-blue-500 hover:text-blue-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <ChevronDown className={`w-4 h-4 text-blue-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
        
        {dropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-xl lg:rounded-2xl shadow-lg max-h-60 overflow-hidden">
            {/* Search box at the top */}
            <div className="p-3 border-b border-blue-100 bg-blue-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search cities..."
                  className="w-full pl-10 pr-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-white text-blue-900 text-sm"
                  autoFocus
                />
              </div>
            </div>
            
            {/* Options list */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(city => (
                  <div
                    key={city.cityID}
                    onClick={() => handleSelect(city)}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors text-blue-900 border-b border-blue-100 last:border-b-0"
                  >
                    {city.cityName}
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-gray-500 text-center">
                  No cities found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Load cities from database
  useEffect(() => {
    const loadCities = async () => {
      try {
        setCitiesLoading(true);
        const response = await cityApi.getCities(true); // Get only active cities
        
        // Handle different response structures
        let citiesData = [];
        if (response?.value && Array.isArray(response.value)) {
          citiesData = response.value;
        } else if (Array.isArray(response)) {
          citiesData = response;
        } else if (response && typeof response === 'object') {
          citiesData = [response];
        }
        
        setCities(citiesData);
      } catch (error) {
        console.error('Error loading cities:', error);
        // Fallback to hardcoded cities if API fails
        setCities([
          { cityID: 1, cityName: 'Colombo', isActive: true },
          { cityID: 2, cityName: 'Badulla', isActive: true },
          { cityID: 3, cityName: 'Trincomalee', isActive: true },
          { cityID: 4, cityName: 'Batticaloa', isActive: true },
          { cityID: 5, cityName: 'Kalmunai', isActive: true },
        ]);
      } finally {
        setCitiesLoading(false);
      }
    };

    loadCities();
  }, []);

  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle different time formats
      if (typeof timeString === 'string') {
        // If it's already in HH:MM format
        if (timeString.match(/^\d{2}:\d{2}$/)) {
          const [hours, minutes] = timeString.split(':');
          const hour12 = parseInt(hours) % 12 || 12;
          const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
          return `${hour12}:${minutes} ${ampm}`;
        }
        // If it's a datetime string
        const date = new Date(timeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          });
        }
      }
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  // Load timetable data when modal opens
  useEffect(() => {
    const loadTimetable = async () => {
      if (!isTimetableModalOpen || !selectedRouteId) {
        setTimetableData([]);
        return;
      }

      try {
        setTimetableLoading(true);
        const response = await routeCityApi.getRouteCitiesByRoute(selectedRouteId);
        const cities = response || [];
        // Sort by order
        const sortedCities = cities.sort((a, b) => (a.order || a.Order || a.orderID || 0) - (b.order || b.Order || b.orderID || 0));
        setTimetableData(sortedCities);
      } catch (error) {
        console.error('Error loading timetable:', error);
        setTimetableData([]);
        Swal.fire('Error', 'Failed to load timetable', 'error');
      } finally {
        setTimetableLoading(false);
      }
    };

    loadTimetable();
  }, [isTimetableModalOpen, selectedRouteId]);

  // Format duration helper function - converts minutes to hours and minutes
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0 min';
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${remainingMinutes} min`;
  };

  // Load popular routes from database
  useEffect(() => {
    const loadPopularRoutes = async () => {
      try {
        setRoutesLoading(true);
        const response = await routeApi.getRoutes(true); // Get only active routes
        
        // Handle different response structures
        let routes = [];
        if (response?.value && Array.isArray(response.value)) {
          routes = response.value;
        } else if (Array.isArray(response)) {
          routes = response;
        } else if (response && typeof response === 'object') {
          routes = [response];
        }
        
        // Take first 4 routes as popular routes
        const routesToProcess = routes.slice(0, 4);
        
        // Fetch schedules to get departure and arrival times
        const schedulesResponse = await scheduleApi.getSchedules();
        let allSchedules = [];
        if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
          allSchedules = schedulesResponse.value;
        } else if (Array.isArray(schedulesResponse)) {
          allSchedules = schedulesResponse;
        }
        
        // Map routes to display format with schedule times
        const popularRoutesData = await Promise.all(
          routesToProcess.map(async (route) => {
            // Calculate total price: basePrice + serviceCharge
            const basePrice = route.basePrice || 0;
            const serviceCharge = route.serviceCharge || 0;
            const totalPrice = basePrice + serviceCharge;
            
            const routeId = route.routeID || route.id;
            
            // Find first schedule for this route
            const routeSchedule = allSchedules.find(s => 
              (s.routeID || s.routeId) === routeId && (s.isActive !== false)
            );
            
            let departure = 'N/A';
            let arrival = 'N/A';
            
            // If schedule found, try to get times from schedule or schedule dates
            if (routeSchedule) {
              const scheduleId = routeSchedule.scheduleID || routeSchedule.scheduleId || routeSchedule.id;
              
              // Try to get times from schedule first
              if (routeSchedule.departureTime) {
                departure = formatTime(routeSchedule.departureTime);
              }
              if (routeSchedule.arrivalTime) {
                arrival = formatTime(routeSchedule.arrivalTime);
              }
              
              // If times not in schedule, try to get from schedule dates
              if (departure === 'N/A' || arrival === 'N/A') {
                try {
                  const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(scheduleId);
                  let scheduleDates = [];
                  
                  if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
                    scheduleDates = scheduleDatesResponse.value;
                  } else if (Array.isArray(scheduleDatesResponse)) {
                    scheduleDates = scheduleDatesResponse;
                  }
                  
                  // Get first available schedule date
                  if (scheduleDates.length > 0) {
                    const firstDate = scheduleDates[0];
                    if (firstDate.departureTime && departure === 'N/A') {
                      departure = formatTime(firstDate.departureTime);
                    }
                    if (firstDate.arrivalTime && arrival === 'N/A') {
                      arrival = formatTime(firstDate.arrivalTime);
                    }
                  }
                } catch (error) {
                  console.warn(`Error fetching schedule dates for route ${routeId}:`, error);
                }
              }
            }
            
            return {
              routeId: routeId,
              fromCityId: route.fromCityID || route.fromCityId,
              toCityId: route.toCityID || route.toCityId,
              from: route.fromCityName || route.fromLocation || 'Unknown',
              to: route.toCityName || route.toLocation || 'Unknown',
              price: `Rs ${totalPrice.toFixed(2)}`,
              duration: formatDuration(route.estimatedDuration || 0),
              departure: departure,
              arrival: arrival,
              rating: 4.5 + Math.random() * 0.5 // Random rating between 4.5-5.0
            };
          })
        );
        
        setPopularRoutes(popularRoutesData);
      } catch (error) {
        console.error('Error loading popular routes:', error);
        // Fallback to hardcoded routes if API fails
        setPopularRoutes([
          { 
            from: 'Colombo', 
            to: 'Badulla', 
            price: 'Rs 2,260.00', 
            duration: '18:00 min',
            departure: 'N/A',
            arrival: 'N/A',
            rating: 4.8 
          },
          { 
            from: 'Colombo', 
            to: 'Trincomalee', 
            price: 'Rs 2,270.00', 
            duration: '17:00 min',
            departure: 'N/A',
            arrival: 'N/A',
            rating: 4.7 
          },
          { 
            from: 'Colombo', 
            to: 'Batticaloa', 
            price: 'Rs 2,800.00', 
            duration: '16:00 min',
            departure: 'N/A',
            arrival: 'N/A',
            rating: 4.9 
          },
          { 
            from: 'Colombo', 
            to: 'Kalmunai', 
            price: 'Rs 2,380.00', 
            duration: '16:30 min',
            departure: 'N/A',
            arrival: 'N/A',
            rating: 4.6 
          },
        ]);
      } finally {
        setRoutesLoading(false);
      }
    };

    loadPopularRoutes();
  }, []);

  // If user is a manager, show manager dashboard
  if (user && isRole(ROLES.MANAGER)) {
    return <ManagerDashboard />;
  }

  const features = [
    {
      icon: Shield,
      title: 'Safe Bus Travel',
      description: 'Your safety is our top priority with well-maintained buses and experienced drivers for every journey.'
    },
    {
      icon: Clock,
      title: 'Punctual Bus Service',
      description: 'We value your time with reliable bus schedules and on-time departures across all routes.'
    },
    {
      icon: Award,
      title: 'Quality Bus Service',
      description: 'Premium buses with comfortable seating, air conditioning, and modern amenities for your comfort.'
    },
    {
      icon: Users,
      title: '24/7 Ticket Support',
      description: 'Round-the-clock customer support to assist you with ticket booking and travel queries.'
    }
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!fromCityId || !toCityId || !travelDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Please select both cities and travel date',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate that travel date is not in the past
    const today = getMinSelectableDate();
    if (travelDate < today) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Date',
        text: 'Travel date cannot be in the past. Please select today\'s date or a future date.',
        confirmButtonColor: '#dc2626'
      });
      return;
    }

    setIsLoading(true);
    setHasSearched(true); // Mark that search has been performed

    try {
      // Search for routes based on cities and date
      const searchData = {
        fromCityId: parseInt(fromCityId),
        toCityId: parseInt(toCityId),
        travelDate: travelDate
      };

      // Use the RouteCities search endpoint
      const response = await routeApi.searchRoutes(searchData);
      
      // Handle different response structures
      let routes = [];
      if (response?.value && Array.isArray(response.value)) {
        routes = response.value;
      } else if (Array.isArray(response)) {
        routes = response;
      } else if (response && typeof response === 'object') {
        routes = [response];
      }

      // Routes are already filtered by backend using RouteCities logic
      const matchingRoutes = routes;

      // Log for debugging
      console.log('Search request:', { fromCityId: parseInt(fromCityId), toCityId: parseInt(toCityId) });
      console.log('Found routes:', matchingRoutes);

      // Helper function (formatTime is already defined at component level)
        const formatTime = (timeString) => {
          if (!timeString) return 'N/A';
          try {
          // If it's already in HH:MM format (e.g., "11:00" or "23:00")
          if (typeof timeString === 'string' && timeString.match(/^\d{1,2}:\d{2}$/)) {
            const [hours, minutes] = timeString.split(':');
            const hour12 = parseInt(hours) % 12 || 12;
            const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
            return `${hour12}:${minutes} ${ampm}`;
          }
          
          // If it's a datetime string, extract time part
          if (timeString.includes('T') || timeString.includes(' ')) {
            const date = new Date(timeString);
            if (!isNaN(date.getTime())) {
              return date.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
            }
          }
          
          // Try to parse as time string
          const date = new Date(`2000-01-01T${timeString}`);
          if (!isNaN(date.getTime())) {
            return date.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
          }
          
          return timeString;
        } catch (error) {
          console.warn('Error formatting time:', timeString, error);
            return timeString;
          }
        };
        
      // Transform routes to search results format with schedule information
      // Fetch schedules for each route to get departure/arrival times
      // Create a separate result for each schedule that has a matching schedule date
      const searchResultsData = [];
      
      for (const route of matchingRoutes) {
        try {
          // Fetch schedules for this route
          const schedulesResponse = await scheduleApi.getSchedules();
          let allSchedules = [];
          
          if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
            allSchedules = schedulesResponse.value;
          } else if (Array.isArray(schedulesResponse)) {
            allSchedules = schedulesResponse;
          }
          
          // Filter schedules for this route
          const routeId = route.routeID || route.id;
          const routeSchedules = allSchedules.filter(s => 
            (s.routeID || s.routeId) === routeId && (s.isActive !== false)
          );
          
          // Process each schedule to find matching schedule dates
          for (const schedule of routeSchedules) {
            let scheduleDate = null;
            let departureTime = 'N/A';
            let arrivalTime = 'N/A';
            
            try {
              const scheduleId = schedule.scheduleID || schedule.id;
              const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(scheduleId);
              let scheduleDates = [];
              
              if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
                scheduleDates = scheduleDatesResponse.value;
              } else if (Array.isArray(scheduleDatesResponse)) {
                scheduleDates = scheduleDatesResponse;
              }
              
              // Find schedule date matching the travel date
              scheduleDate = scheduleDates.find(sd => {
                const dateStr = sd.date?.split('T')[0] || sd.date;
                return dateStr === travelDate;
              });
              
              // Only create a result if we found a matching schedule date for the travel date
              if (scheduleDate) {
                // Extract times from schedule date
                departureTime = formatTime(scheduleDate.departureTime || scheduleDate.departure);
                arrivalTime = formatTime(scheduleDate.arrivalTime || scheduleDate.arrival);
                
                // Get vehicle registration number
                const vehicleNumber = schedule.vehicle?.registrationNumber || schedule.vehicle?.busNumber || 'N/A';
                
                // Calculate total price: ticket price + service charge
                const ticketPrice = scheduleDate.price || route.basePrice || 0;
                const serviceCharge = route.serviceCharge || 0;
                const totalPrice = ticketPrice + serviceCharge;
                
                // Create a result entry for this schedule
                searchResultsData.push({
                  id: `${route.routeID}_${schedule.scheduleID || schedule.id}`,
          route: `${route.fromLocation || 'Unknown'} → ${route.toLocation || 'Unknown'}`,
                  departure: departureTime,
                  arrival: arrivalTime,
          duration: formatDuration(route.estimatedDuration || 0),
                  price: `Rs ${totalPrice.toFixed(2)}`,
                  busType: schedule.vehicle?.busType || schedule.busType || 'Standard',
                  vehicleNumber: vehicleNumber,
                  availableSeats: scheduleDate.availableSeats || 0,
                  totalSeats: schedule.vehicle?.totalSeats || 0,
                  scheduleId: schedule.scheduleID || schedule.id,
                  scheduleDateId: scheduleDate.scheduleDateID || scheduleDate.scheduleDateId,
          routeId: route.routeID,
                  vehicleId: schedule.busID || schedule.vehicleId || null,
                  scheduleName: schedule.scheduleName || 'Standard'
                });
              }
            } catch (scheduleDateError) {
              console.warn(`Could not fetch schedule dates for schedule ${schedule.scheduleID}:`, scheduleDateError);
            }
          }
        } catch (scheduleError) {
          console.warn(`Could not fetch schedules for route ${route.routeID}:`, scheduleError);
        }
      }

      setSearchResults(searchResultsData);
      // Results are displayed below the search form, no need for alerts
      
      // Scroll to results section after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (resultsSectionRef.current) {
          resultsSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error searching routes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Search Failed',
        text: 'Failed to search for routes. Please try again.',
        confirmButtonColor: '#dc2626'
      });
      setSearchResults([]);
      setHasSearched(true); // Still mark as searched even on error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-screen min-w-screen max-w-screen min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white py-16 lg:py-24 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"></div>
        </div>
        
        {/* Banner Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/banner.webp" 
            alt={`${COMPANY_CONFIG.name} Bus Service Banner`} 
            className="w-full h-full object-cover opacity-60"
          />
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        {/* Important Notification Banner */}
        {/*<div className="w-full px-4 sm:px-6 lg:px-8 relative z-20 mb-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 border-4 border-yellow-600 rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-8 animate-pulse">
              <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-red-700 flex-shrink-0 animate-bounce" />
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-red-900 mb-1 sm:mb-2">
                    ⚠️ IMPORTANT NOTICE ⚠️
                  </h3>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold text-red-800 leading-tight">
                    Don't Make the Payment - Booking System Will Come Soon!
                  </p>
                </div>
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-red-700 flex-shrink-0 animate-bounce" />
              </div>
            </div>
          </div>
        </div>*/}
        
        <div className="w-full px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-6xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 lg:mb-8 leading-tight drop-shadow-lg">
              Welcome to
              <br />
              <span className="text-yellow-300 drop-shadow-lg">{COMPANY_CONFIG.name}</span>
              <br />
              <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl drop-shadow-lg">{COMPANY_CONFIG.tagline}</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 lg:mb-12 text-blue-100 max-w-4xl mx-auto leading-relaxed drop-shadow-lg">
              Book your bus tickets online for anywhere in Sri Lanka with just a few clicks
            </p>
            
            {/* Booking Form - Hidden */}
            <div className="w-full max-w-5xl mx-auto" style={{ display: 'none' }}>
              <form onSubmit={handleSearch} className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                  <div>
                    <SearchableDropdown
                      label="From"
                      icon={<MapPin className="w-4 h-4 inline mr-2 text-blue-600" />}
                      placeholder="Select departure city"
                      value={fromCityId}
                      searchTerm={fromSearchTerm}
                      setSearchTerm={setFromSearchTerm}
                      dropdownOpen={fromDropdownOpen}
                      setDropdownOpen={setFromDropdownOpen}
                      dropdownRef={fromDropdownRef}
                      onChange={setFromCityId}
                      options={cities}
                      loading={citiesLoading}
                    />
                  </div>
                  
                  <div>
                    <SearchableDropdown
                      label="To"
                      icon={<MapPin className="w-4 h-4 inline mr-2 text-blue-600" />}
                      placeholder="Select destination city"
                      value={toCityId}
                      searchTerm={toSearchTerm}
                      setSearchTerm={setToSearchTerm}
                      dropdownOpen={toDropdownOpen}
                      setDropdownOpen={setToDropdownOpen}
                      dropdownRef={toDropdownRef}
                      onChange={setToCityId}
                      options={cities}
                      loading={citiesLoading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2 lg:mb-3">
                      <Calendar className="w-4 h-4 inline mr-2 text-blue-600" />
                      Date (DD/MM/YYYY)
                    </label>
                    <div className="relative date-picker-wrapper">
                      <input
                        type="date"
                        value={travelDate}
                        onChange={(e) => {
                          const selectedDateValue = e.target.value;
                          const today = getMinSelectableDate();
                          if (selectedDateValue >= today) {
                            setTravelDate(selectedDateValue);
                          }
                        }}
                        min={getMinSelectableDate()}
                        max={getMaxSelectableDate()}
                        className="w-full px-4 py-3 lg:py-4 border border-blue-400 rounded-xl lg:rounded-2xl focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-blue-50 text-blue-900 text-base lg:text-lg transition-all duration-200"
                        required
                        style={{
                          colorScheme: 'light'
                        }}
                      />
                      <input
                        type="text"
                        value={formatDateForDisplay(travelDate)}
                        readOnly
                        placeholder="DD/MM/YYYY"
                        className="w-full px-4 py-3 lg:py-4 border border-blue-400 rounded-xl lg:rounded-2xl focus:ring-2 focus:ring-blue-600 focus:border-blue-500 bg-blue-50 text-blue-900 text-base lg:text-lg transition-all duration-200"
                      />
                      <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500 pointer-events-none" />
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 lg:mt-8 text-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-blue-600 text-white px-8 py-4 lg:px-12 lg:py-5 rounded-xl lg:rounded-2xl text-lg lg:text-xl font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto flex items-center justify-center mx-auto space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Searching for tickets...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        <span>Search Tickets</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Search Results - Hidden */}
      {false && searchResults.length > 0 && (
        <section ref={resultsSectionRef} className="w-screen min-w-screen max-w-screen py-8 lg:py-12 bg-gray-50">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 text-center">
                  Available Bus Tickets
                </h2>
                {searchResults.map((result) => (
                  <div key={result.id} className="border border-gray-200 rounded-xl p-6 mb-4 hover:shadow-lg transition-shadow">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-center">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{result.route}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {result.duration}
                          </span>
                          <span className="flex items-center">
                            <Bus className="w-4 h-4 mr-1" />
                            {result.busType}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <Bus className="w-4 h-4 mr-1 text-blue-600" />
                          <span className="font-medium">Vehicle: {result.vehicleNumber}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-1 text-blue-600" />
                          <span className="font-medium">Travel Date: {formatDateForDisplay(travelDate)}</span>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 mb-1">{result.price}</div>
                        <div className="text-sm text-gray-500 mb-1">
                          {result.availableSeats} / {result.totalSeats} seats available
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (result.availableSeats / (result.totalSeats || 1)) > 0.6 
                              ? 'bg-green-500' 
                              : (result.availableSeats / (result.totalSeats || 1)) > 0.3 
                              ? 'bg-yellow-500' 
                              : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, (result.availableSeats / (result.totalSeats || 1)) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="space-y-2">
                          <div className="flex items-center justify-center space-x-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <div>
                              <div className="text-xs text-gray-500">Departure</div>
                              <div className="text-sm font-semibold text-gray-900">{result.departure}</div>
                            </div>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <Clock className="w-4 h-4 text-green-600" />
                            <div>
                              <div className="text-xs text-gray-500">Arrival</div>
                              <div className="text-sm font-semibold text-gray-900">{result.arrival}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <Link
                          to={`/book?routeId=${result.routeId}&date=${travelDate}&scheduleId=${result.scheduleId || ''}`}
                          state={{
                              fromCityId: fromCityId,
                              toCityId: toCityId,
                              travelDate: travelDate,
                              routeId: result.routeId,
                              scheduleId: result.scheduleId,
                              vehicleId: result.vehicleId,
                              route: result.route,
                              price: result.price,
                              busType: result.busType,
                              duration: result.duration,
                              departure: result.departure,
                              arrival: result.arrival,
                              availableSeats: result.availableSeats,
                              totalSeats: result.totalSeats
                          }}
                          className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium inline-block"
                        >
                          Book Now
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* No Results Message - Only show after search button is clicked */}
      {!isLoading && hasSearched && searchResults.length === 0 && (
        <section ref={resultsSectionRef} className="w-screen min-w-screen max-w-screen py-8 lg:py-12 bg-gray-50">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                <div className="text-center py-8">
                  <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    No Routes Found
                  </h2>
                  <p className="text-lg text-gray-600 mb-2">
                    No routes found between the selected cities for the selected date.
                  </p>
                  <p className="text-sm text-gray-500">
                    Please try different cities or select a different date.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Popular Routes Section */}
      <section className="w-screen min-w-screen max-w-screen py-16 lg:py-24 bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
                Popular Bus Routes
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
                Most traveled routes with competitive ticket prices
              </p>
            </div>

            {routesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {[1, 2, 3, 4].map((index) => (
                  <div key={index} className="bg-white rounded-2xl lg:rounded-3xl shadow-lg p-6 lg:p-8 border border-gray-100 animate-pulse">
                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-gray-300 rounded"></div>
                        <div className="w-16 h-4 bg-gray-300 rounded"></div>
                      </div>
                      <div className="w-12 h-4 bg-gray-300 rounded"></div>
                    </div>
                    <div className="w-full h-6 bg-gray-300 rounded mb-3 lg:mb-4"></div>
                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                      <div className="w-20 h-4 bg-gray-300 rounded"></div>
                      <div className="w-24 h-6 bg-gray-300 rounded"></div>
                    </div>
                    <div className="w-full h-12 bg-gray-300 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {popularRoutes.map((route, index) => (
                  <div key={index} className="bg-white rounded-2xl lg:rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 lg:p-8 border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1 flex flex-col h-full relative">
                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
                        <span className="text-sm lg:text-base text-gray-500">Route</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-400 fill-current" />
                        <span className="text-sm lg:text-base text-gray-600">{route.rating.toFixed(1)}</span>
                      </div>
                    </div>

                    {/* Fixed height title section */}
                    <div className="mb-3 lg:mb-4 flex flex-col">
                      <h3 className="text-lg lg:text-xl xl:text-2xl font-semibold text-gray-900 leading-tight line-clamp-2 mb-1">
                        {route.from} to {route.to}
                      </h3>
                      <p className="text-xs lg:text-sm text-gray-500 font-medium">
                        Nuwantha Enterprises (Pvt) Ltd
                      </p>
                    </div>

                    {/* Departure and Arrival Times */}
                    <div className="flex items-center justify-between mb-3 lg:mb-4">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <div className="text-xs text-gray-500">Departure</div>
                        <span className="text-sm font-semibold text-gray-900">{route.departure}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <div className="text-xs text-gray-500">Arrival</div>
                        <span className="text-sm font-semibold text-gray-900">{route.arrival}</span>
                      </div>
                    </div>

                    {/* Duration and Price */}
                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
                        <span className="text-sm lg:text-base text-gray-600">{route.duration}</span>
                      </div>
                      <span className="text-lg lg:text-xl xl:text-2xl font-bold text-blue-600">{route.price}</span>
                    </div>

                    {/* Push button to bottom */}
                    <div className="mt-auto space-y-2">
                      <button
                        onClick={() => {
                          setSelectedRouteId(route.routeId);
                          setIsTimetableModalOpen(true);
                        }}
                        className="bg-green-600 text-white py-1 px-2 rounded-lg text-xs hover:bg-green-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                        title="View Timetable"
                      >
                        Timetable
                      </button>
                      <Link
                        to={{
                          pathname: "/book",
                          search: `?routeId=${route.routeId}&date=${getSriLankaDateString()}`,
                          state: {
                            fromCityId: route.fromCityId,
                            toCityId: route.toCityId,
                            routeId: route.routeId,
                            travelDate: getSriLankaDateString(),
                            from: route.from,
                            to: route.to
                          }
                        }}
                        className="w-full bg-blue-600 text-white py-3 lg:py-4 px-4 lg:px-6 rounded-xl lg:rounded-2xl text-center block hover:bg-blue-700 transition-all duration-200 font-medium text-base lg:text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        Book Ticket
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="text-center mt-12 lg:mt-16">
              <Link
                to="/routes"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg lg:text-xl transition-colors"
              >
                View All Routes
                <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Hidden (commented out) */}
      {/* <section className="w-screen min-w-screen max-w-screen py-16 lg:py-24 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
                Why Choose {COMPANY_CONFIG.name} Bus Service?
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
                We provide the best bus travel experience in Sri Lanka
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="text-center group">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 lg:mb-6 group-hover:bg-blue-600 transition-all duration-300 transform group-hover:scale-110">
                      <Icon className="w-8 h-8 lg:w-10 lg:h-10 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-3 lg:mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="w-screen min-w-screen max-w-screen py-16 lg:py-24 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 lg:mb-8">
              Ready to Book Your Bus Ticket with {COMPANY_CONFIG.name}?
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl mb-8 lg:mb-12 text-blue-100 max-w-4xl mx-auto">
              Book your bus tickets now and enjoy a comfortable journey across Sri Lanka
            </p>
            <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center">
              <Link
                to="/book"
                className="bg-white text-blue-600 px-8 py-4 lg:px-12 lg:py-5 rounded-xl lg:rounded-2xl text-lg lg:text-xl font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Book Bus Tickets Now
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 lg:px-12 lg:py-5 rounded-xl lg:rounded-2xl text-lg lg:text-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Timetable Modal */}
      {isTimetableModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsTimetableModalOpen(false)}
            />
            
            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Route Timetable
                      </h3>
                      <p className="text-blue-100 text-sm">
                        City stops and arrival times
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsTimetableModalOpen(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 max-h-96 overflow-y-auto">
                {timetableLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading timetable...</span>
                  </div>
                ) : timetableData.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">No timetable data available for this route.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timetableData.map((city, index) => (
                      <div 
                        key={city.routeCityID || index}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-900">
                            {city.cityName || city.City?.cityName || 'Unknown City'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-5 h-5 text-gray-400" />
                          <span className="font-semibold text-gray-700">
                            {formatTime(city.arrivalTime || city.ArrivalTime)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => setIsTimetableModalOpen(false)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 